import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Button, Dropdown, Menu, Modal, Tooltip } from 'antd';
import {
  ExclamationCircleOutlined,
  ExportOutlined,
  MoreOutlined,
} from '@ant-design/icons';
// Api
import { UPDATE_ARTICLE } from 'api/articles/mutations';
// Helpers
import { getArticlePageUrl } from 'helpers/articles';
// Types
import { GetArticles_getArticles_entities } from 'api/articles/types/GetArticles';
import {
  UpdateArticle,
  UpdateArticleVariables,
} from 'api/articles/types/UpdateArticle';
import { ArticleStatus } from 'api/graphql-global-types';
// UI
import { errorNotification, successNotification } from 'ui/Notification';
// Styles
import styles from './Actions.module.scss';

const { confirm } = Modal;

type ArticleActionsProps = {
  record: GetArticles_getArticles_entities;
  onChanged: () => void;
  onEdit: (record: GetArticles_getArticles_entities) => Promise<void>;
};

const ArticleActions = ({
  record,
  onChanged,
  onEdit,
}: ArticleActionsProps): JSX.Element => {
  const [isOpeningEdit, setIsOpeningEdit] = useState<boolean>(false);

  const [updateArticle] = useMutation<UpdateArticle, UpdateArticleVariables>(
    UPDATE_ARTICLE
  );

  const updateArticleStatus = async (status: ArticleStatus) => {
    try {
      await updateArticle({
        variables: {
          input: {
            id: record.id,
            status,
          },
        },
      });
      const newStatus =
        status === ArticleStatus.Active ? 'Published' : 'Deactivated';
      successNotification(
        `Article status successfully changed to ${newStatus}`
      );
      onChanged();
    } catch (error) {
      errorNotification((error as Error)?.message || 'Something went wrong');
    }
  };

  const onPublishArticle = () => {
    confirm({
      title: 'Are you sure you want to publish this article?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes',
      okType: 'primary',
      cancelText: 'No',
      onOk: () => updateArticleStatus(ArticleStatus.Active),
    });
  };

  const onDeactivateArticle = () => {
    confirm({
      title: 'Are you sure you want to deactivate this article?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: () => updateArticleStatus(ArticleStatus.Disabled),
    });
  };

  const handleEdit = async () => {
    setIsOpeningEdit(true);
    try {
      await onEdit(record);
    } catch (error) {
      errorNotification((error as Error)?.message || 'Something went wrong');
    } finally {
      setIsOpeningEdit(false);
    }
  };

  const isPublished = record.status === ArticleStatus.Active;
  const articlePageUrl = isPublished ? getArticlePageUrl(record) : null;

  // not RowActions: the workspace theme fills a row's first button dark
  return (
    <div className={styles.actions}>
      <Button
        size="small"
        className={styles.editBtn}
        loading={isOpeningEdit}
        onClick={handleEdit}
      >
        Edit
      </Button>
      <Tooltip
        title={articlePageUrl ? undefined : 'Only published articles are live'}
      >
        <Button
          size="small"
          className={styles.quietBtn}
          icon={<ExportOutlined />}
          href={articlePageUrl || undefined}
          target="_blank"
          rel="noopener noreferrer"
          disabled={!articlePageUrl}
        >
          Open article
        </Button>
      </Tooltip>
      <Dropdown
        trigger={['click']}
        placement="bottomRight"
        overlay={
          <Menu selectable={false}>
            {isPublished ? (
              <Menu.Item key="deactivate" danger onClick={onDeactivateArticle}>
                Deactivate
              </Menu.Item>
            ) : (
              <Menu.Item key="activate" onClick={onPublishArticle}>
                Activate
              </Menu.Item>
            )}
          </Menu>
        }
      >
        <Button
          size="small"
          className={styles.quietBtn}
          aria-label="More actions"
          aria-haspopup="menu"
          icon={<MoreOutlined />}
        />
      </Dropdown>
    </div>
  );
};

export default ArticleActions;
